import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styles from './StudentReport.module.css';
import apiClient from '../../api/client';
import { formatCairoDate } from '../../utils/cairoDate';

const StudentReport = () => {
  const { id } = useParams(); // بناخد الـ ID بتاع الطالب من الرابط
  const [report, setReport] = useState({
    student: null,
    attendance: [],
    grades: [],
    summary: {
      attendance: { total: 0, present: 0, absent: 0 },
      exams: { online: { count: 0 }, paper: { count: 0 } },
    },
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get(`/students/${id}/report`)
      .then(res => {
        setReport(res.data);
        setLoading(false);
      })
      .catch(err => {
        console.error("Error fetching report:", err);
        setLoading(false);
      });
  }, [id]);

  if (loading) return <div>جاري تحميل التقرير...</div>;

  const { student, attendance, grades, summary } = report;

  // بناء رسالة لولي الأمر برقم الحضور والغياب، وفتحها في واتساب
  const handleSendToParent = () => {
    if (!student?.parentPhone) {
      alert('رقم هاتف ولي الأمر غير مسجل لهذا الطالب');
      return;
    }

    const present = summary.attendance.present || 0;
    const absent = summary.attendance.absent || 0;
    const total = summary.attendance.total || 0;
    const attendanceRate = total > 0 ? (present / total) * 100 : 100;

    // حالة الانضباط بتتحسب تلقائي على أساس نسبة الحضور
    let disciplineStatus = 'ممتاز';
    if (attendanceRate < 60) disciplineStatus = 'يحتاج إلى متابعة';
    else if (attendanceRate < 75) disciplineStatus = 'جيد';
    else if (attendanceRate < 90) disciplineStatus = 'جيد جدًا';

    const message =
`📚 سلسلة الأزهري في العلوم الشرعية
من معلمي المواد الشرعية:
أ/ حسام صبري الأزهري
أ/ محمد كامل الشيمي
إلى السيد/ ولي أمر الطالب: ${student.name}
السلام عليكم ورحمة الله وبركاته.
حرصًا منا على المتابعة المستمرة لأبنائنا الطلاب، نحيط سيادتكم علمًا بموقف نجلكم/ابنتكم من الحضور والانضباط خلال هذا الشهر:
📅 عدد أيام الحضور: ${present}
📅 عدد أيام الغياب: ${absent}
📌 حالة الانضباط: ${disciplineStatus}
ونرجو من سيادتكم التعاون معنا في متابعة انتظام الطالب في الحضور؛ لما لذلك من أثرٍ كبير في التحصيل العلمي والتفوق.
نسأل الله أن يوفق أبناءنا جميعًا، وأن يبارك في جهودكم.
مع خالص الشكر والتقدير،
سلسلة الأزهري في العلوم الشرعية`;

    // تجهيز الرقم لواتساب: شيل الصفر من الأول وضيف كود مصر لو مش موجود
    let phone = student.parentPhone.replace(/\D/g, '');
    if (phone.startsWith('0')) phone = phone.slice(1);
    if (!phone.startsWith('20')) phone = `20${phone}`;

    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={`${styles.reportHeader} ${styles.noPrint}`}>
        <h1>تقرير الطالب{student ? `: ${student.name}` : ''}</h1>
        <div className={styles.headerActions}>
          <button className={styles.printBtn} onClick={() => window.print()}>
            طباعة التقرير 🖨️
          </button>
          <button className={styles.sendBtn} onClick={handleSendToParent}>
            إرسال التقرير لولي الأمر 📲
          </button>
        </div>
      </div>

      {/* عنوان بيظهر عند الطباعة بس (مبيتحطش على الشاشة عادي) */}
      <div className={styles.printOnly}>
        <h1>تقرير الطالب: {student?.name}</h1>
        <p>كود الطالب: {student?.code} — المرحلة: {student?.grade} — المجموعة: {student?.group}</p>
        <p>تاريخ الطباعة: {formatCairoDate(new Date())}</p>
      </div>

      {/* ملخص الحضور والغياب */}
      <div className={styles.section}>
        <h2>ملخص الحضور</h2>
        <div className={styles.summaryCards}>
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>إجمالي الحصص</span>
            <span className={styles.summaryValue}>{summary.attendance.total}</span>
          </div>
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>حضر</span>
            <span className={styles.summaryValue}>{summary.attendance.present}</span>
          </div>
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>غاب</span>
            <span className={styles.summaryValue}>{summary.attendance.absent}</span>
          </div>
        </div>
      </div>

      {/* قسم الحضور */}
      <div className={styles.section}>
        <h2>سجل الحضور والغياب</h2>
        <table className={styles.table}>
          <thead>
            <tr><th>التاريخ</th><th>الحالة</th></tr>
          </thead>
          <tbody>
            {attendance.map(item => (
              <tr key={item._id}>
                <td>{formatCairoDate(item.date)}</td>
                <td>{item.status === 'present' ? '✅ حاضر' : '❌ غائب'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ملخص الامتحانات: أونلاين وورقي كل واحد لوحده من غير ما يترابطوا */}
      <div className={styles.section}>
        <h2>ملخص الامتحانات</h2>
        <div className={styles.summaryCards}>
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>امتحانات أونلاين</span>
            <span className={styles.summaryValue}>{summary.exams.online.count}</span>
          </div>
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>امتحانات ورقي</span>
            <span className={styles.summaryValue}>{summary.exams.paper.count}</span>
          </div>
        </div>
      </div>

      {/* قسم الدرجات */}
      <div className={styles.section}>
        <h2>سجل الدرجات</h2>
        <table className={styles.table}>
          <thead>
            <tr><th>الامتحان</th><th>النوع</th><th>الدرجة</th></tr>
          </thead>
          <tbody>
            {grades.map(item => (
              <tr key={item._id}>
                <td>{item.exam?.name || "امتحان غير معروف"}</td>
                <td>{item.type === 'online' ? 'أونلاين' : 'ورقي'}</td>
                <td>{item.score}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StudentReport;
